import { Button, Flex, Input, Spacing } from "@p0security/ui";
import { useCallback } from "react";
import type { ControllerProps } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";

const emailPattern =
  /^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/i;

export type MagicLinkLoginInputs = {
  email: string;
};

export type MagicLinkLoginFormProps = {
  onLogin: (args: MagicLinkLoginInputs) => void;
};

export const MagicLinkLoginForm: React.FC<MagicLinkLoginFormProps> = ({
  onLogin,
}) => {
  const { control, handleSubmit } = useForm<MagicLinkLoginInputs>();

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      void handleSubmit((data) => onLogin(data))(e);
    },
    [handleSubmit, onLogin]
  );

  const renderEmail = useCallback<
    ControllerProps<MagicLinkLoginInputs, "email">["render"]
  >(
    ({ field }) => <Input {...field} placeholder="Your organization email" />,
    []
  );

  return (
    <form onSubmit={onSubmit} className="text-center">
      <Controller
        name="email"
        control={control}
        rules={{
          required: true,
          pattern: emailPattern,
        }}
        render={renderEmail}
      />
      <Spacing mt="1em">
        <Button type="primary" htmlType="submit">
          Login
        </Button>
      </Spacing>
    </form>
  );
};

export type PasswordFormProps = {
  showForm: PasswordFormType;
  setShowForm: (type: PasswordFormType) => void;
  onLogin: (args: PasswordLoginInputs) => void;
  onSignUp: (args: PasswordSignUpInputs) => void;
  onForgotPassword: (args: ForgotPasswordInputs) => void;
};

export type PasswordFormType = "forgot" | "login" | "signup";

export const PasswordForm: React.FC<PasswordFormProps> = ({
  showForm,
  setShowForm,
  onLogin,
  onSignUp,
  onForgotPassword,
}) => {
  const handleChangeForm = useCallback(
    (type: PasswordFormType) => {
      return () => setShowForm(type);
    },
    [setShowForm]
  );

  if (showForm === "login") {
    return (
      <PasswordLoginForm
        onLogin={onLogin}
        onSignUpClick={handleChangeForm("signup")}
        onForgotPasswordClick={handleChangeForm("forgot")}
      />
    );
  }
  if (showForm === "signup") {
    return (
      <PasswordSignUpForm
        onSignUp={onSignUp}
        onLoginClick={handleChangeForm("login")}
      />
    );
  }
  if (showForm === "forgot") {
    return (
      <ForgotPasswordForm
        onForgotPassword={onForgotPassword}
        onLoginClick={handleChangeForm("login")}
      />
    );
  }

  return <div>Unknown form</div>;
};

export type PasswordLoginInputs = {
  email: string;
  password: string;
};

export type PasswordLoginFormProps = {
  onLogin: (args: PasswordLoginInputs) => void;
  onSignUpClick: () => void;
  onForgotPasswordClick: () => void;
};

export const PasswordLoginForm: React.FC<PasswordLoginFormProps> = ({
  onLogin,
  onSignUpClick,
  onForgotPasswordClick,
}) => {
  const { control, handleSubmit } = useForm<PasswordLoginInputs>({
    mode: "onChange",
  });

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      void handleSubmit((data) => onLogin(data))(e);
    },
    [handleSubmit, onLogin]
  );

  const renderEmail = useCallback<
    ControllerProps<PasswordLoginInputs, "email">["render"]
  >(
    ({ field }) => <Input {...field} placeholder="Your organization email" />,
    []
  );

  const renderPassword = useCallback<
    ControllerProps<PasswordLoginInputs, "password">["render"]
  >(
    ({ field }) => (
      <Input {...field} placeholder="Your password" type="password" />
    ),
    []
  );

  return (
    <Flex
      align="center"
      component="form"
      gap="1rem"
      justify="center"
      onSubmit={onSubmit}
      vertical
    >
      <div className="w-full">
        <div>Email</div>
        <Controller
          name="email"
          control={control}
          rules={{
            required: true,
            pattern: emailPattern,
          }}
          render={renderEmail}
        />
      </div>

      <div className="w-full">
        <Flex justify="space-between" align="flex-end" gap="1rem">
          <div>Password</div>
          <Spacing asChild m={0} mb="-6px">
            <Button
              type="link"
              tabIndex={-1}
              onClick={onForgotPasswordClick}
              /* eslint-disable-line react/forbid-component-props */ style={{
                padding: 0,
              }}
            >
              Forgot password?
            </Button>
          </Spacing>
        </Flex>
        <Controller
          name="password"
          control={control}
          rules={{
            required: true,
          }}
          render={renderPassword}
        />
      </div>
      <Flex className="w-full" gap="1rem">
        <Button type="primary" variant="filled" htmlType="submit">
          Login
        </Button>
        <Button variant="outlined" onClick={onSignUpClick}>
          Create an account
        </Button>
      </Flex>
    </Flex>
  );
};

export type PasswordSignUpInputs = {
  email: string;
  password: string;
  confirm: string;
};

export type PasswordSignUpFormProps = {
  onSignUp: (args: PasswordSignUpInputs) => void;
  onLoginClick: () => void;
};

export const PasswordSignUpForm: React.FC<PasswordSignUpFormProps> = ({
  onSignUp,
  onLoginClick,
}) => {
  const renderEmail = useCallback<
    ControllerProps<PasswordSignUpInputs, "email">["render"]
  >(
    ({ field }) => <Input {...field} placeholder="Your organization email" />,
    []
  );

  const renderPassword = useCallback<
    ControllerProps<PasswordSignUpInputs, "password">["render"]
  >(
    ({ field }) => (
      <Input {...field} placeholder="Your password" type="password" />
    ),
    []
  );
  const renderConfirm = useCallback<
    ControllerProps<PasswordSignUpInputs, "confirm">["render"]
  >(
    ({ field }) => (
      <Input {...field} placeholder="Confirm your password" type="password" />
    ),
    []
  );
  const { control, handleSubmit } = useForm<PasswordSignUpInputs>();
  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      void handleSubmit((data) => onSignUp(data))(e);
    },
    [handleSubmit, onSignUp]
  );

  return (
    <Flex
      align="center"
      component="form"
      gap="1rem"
      justify="center"
      onSubmit={onSubmit}
      vertical
    >
      <div className="w-full">
        <div>Email</div>
        <Controller
          name="email"
          control={control}
          rules={{
            required: true,
            pattern: emailPattern,
          }}
          render={renderEmail}
        />
      </div>

      <div className="w-full">
        <Flex justify="space-between" align="flex-baseline" gap="1rem">
          <div>Password</div>
        </Flex>
        <Controller
          name="password"
          control={control}
          rules={{
            required: true,
          }}
          render={renderPassword}
        />
      </div>

      <div className="w-full">
        <div>Confirm Password</div>
        <Controller
          name="confirm"
          control={control}
          rules={{
            required: true,
          }}
          render={renderConfirm}
        />
      </div>

      <Flex className="w-full" gap="1rem">
        <Button type="primary" variant="filled" htmlType="submit">
          Sign up
        </Button>
        <Button variant="outlined" htmlType="button" onClick={onLoginClick}>
          Back to login
        </Button>
      </Flex>
    </Flex>
  );
};

export type ForgotPasswordInputs = {
  email: string;
};
export type ForgotPasswordFormProps = {
  onForgotPassword: (args: ForgotPasswordInputs) => void;
  onLoginClick: () => void;
};
export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onForgotPassword,
  onLoginClick,
}) => {
  const { control, handleSubmit } = useForm<ForgotPasswordInputs>();
  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      void handleSubmit((data) => onForgotPassword(data))(e);
    },
    [handleSubmit, onForgotPassword]
  );

  const renderEmail = useCallback<
    ControllerProps<ForgotPasswordInputs, "email">["render"]
  >(
    ({ field }) => <Input {...field} placeholder="Your organization email" />,
    []
  );

  return (
    <Flex
      align="center"
      className="w-full"
      component="form"
      gap="1rem"
      onSubmit={onSubmit}
      vertical
    >
      <Controller
        name="email"
        control={control}
        rules={{
          required: true,
          pattern: emailPattern,
        }}
        render={renderEmail}
      />
      <Flex className="w-full" gap="1rem">
        <Button type="primary" htmlType="submit">
          Send password reset email
        </Button>
        <Button variant="outlined" htmlType="button" onClick={onLoginClick}>
          Back to login
        </Button>
      </Flex>
    </Flex>
  );
};
